import {
  bySubstance,
  countLabel,
  firstSentences,
  kitPartCss,
  panel,
  panelCss,
} from '../armKit';
import { worksCount } from '../profileFacts';
import { EDGE, INK, MUTED } from '../surface';

import type { ArmContext, ArmSpec } from '../armRegistry';
import type { ProfileCard } from '../surface';

type Extract = {
  workId: number;
  hasDownloadable: boolean;
  title: string;
  url: string;
  text: string;
};

const DIGEST_SIZE = 3;
const SENTENCES = 2;
const TITLE = 'Selected works:';
const TITLE_SOURCE = 'profile-liautotest-digest-title';
const NOTE_CLASS = 'profile-liautotest-digest__note';
const ITEM_CLASS = 'profile-liautotest-digest__item';
const LINK_CLASS = 'profile-liautotest-digest__title';
const TEXT_CLASS = 'profile-liautotest-digest__text';

// Own classes only, beyond the panel frame: no armKit class and no page class
// is named here, so a change to either is a change in armKit or surface,
// never here.
function armCss(scope: string): string {
  return `${panelCss(scope, { padding: '16px', marginBottom: '0' })}${kitPartCss(
    'panel',
    scope,
    'border-color: #dddde2; border-radius: 7px;',
  )}
${scope} .${NOTE_CLASS} {
  font-size: 13px;
  color: ${MUTED};
  margin: 0 0 16px;
}
${scope} .${ITEM_CLASS} {
  margin-bottom: 16px;
}
${scope} .${ITEM_CLASS}:last-child {
  margin-bottom: 0;
}
${scope} .${ITEM_CLASS} + .${ITEM_CLASS} {
  border-top: 1px solid ${EDGE};
  padding-top: 16px;
}
${scope} .${LINK_CLASS} {
  display: inline-block;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.35;
  color: ${INK};
  margin: 0 0 6px;
}
${scope} .${TEXT_CLASS} {
  font-size: 16px;
  line-height: 1.7;
  color: ${INK};
  margin: 0;
}
`;
}

function subtitleFor(count: number | null): string {
  const total = count == null ? null : countLabel(count, 'paper');

  return total == null
    ? 'The opening lines of three papers.'
    : `The opening lines of three of ${total}.`;
}

function extractOf(card: ProfileCard): Extract[] {
  const text = firstSentences(card.abstractText(), SENTENCES);
  if (text === '' || card.url == null) return [];

  return [
    {
      workId: card.workId,
      hasDownloadable: card.hasDownloadable,
      title: card.title,
      url: card.url,
      text,
    },
  ];
}

function itemFor(extract: Extract, ctx: ArmContext): HTMLElement {
  const item = document.createElement('div');
  item.className = ITEM_CLASS;

  const link = document.createElement('a');
  link.className = LINK_CLASS;
  link.href = extract.url;
  link.textContent = extract.title;
  link.addEventListener('click', () => {
    ctx.track(TITLE_SOURCE, { entityId: extract.workId });
  });
  item.appendChild(link);

  const text = document.createElement('p');
  text.className = TEXT_CLASS;
  text.textContent = extract.text;
  item.appendChild(text);

  return item;
}

export const authorDigest: ArmSpec = {
  onReady: async (ctx) => {
    const facts = await ctx.workFacts();
    if (facts.size === 0) return;

    const extracts = ctx.cards().flatMap(extractOf);
    if (extracts.length < DIGEST_SIZE) return;

    const picks = extracts
      .sort(bySubstance((workId) => facts.get(workId)?.pageCount))
      .slice(0, DIGEST_SIZE);

    const slot = ctx.mountSlot('columnTop');
    if (slot == null) return;

    ctx.injectStyles(armCss(ctx.scope));

    const { root, body } = panel({ title: TITLE });

    // The digest's own note line rather than the kit subtitle: the count below
    // has to update it in place once the real works count resolves, and a
    // kit-owned element is not this arm's to query back.
    const note = document.createElement('p');
    note.className = NOTE_CLASS;
    note.textContent = subtitleFor(null);
    body.appendChild(note);

    picks.forEach((pick) => body.appendChild(itemFor(pick, ctx)));
    slot.appendChild(root);

    // Additive only: the digest is already whole, and a count that never
    // arrives leaves it reading exactly as it rendered.
    void worksCount().then((count) => {
      if (count == null || count < DIGEST_SIZE) return;
      note.textContent = subtitleFor(count);
    });
  },
};
